import { ReactComponent as ChevronLeftIcon } from '@assets/images/app/chevron-left-nutral90.svg';
import { ReactComponent as ChevronRightIcon } from '@assets/images/app/chevron-right-nutral90.svg';
import { ReactComponent as ChevronUpIcon } from '@assets/images/app/chevron-up-nutral90.svg';
import { NumberEdit } from '@components/common/NumberEdit';
import { CommerceFixedOptionItem, ProductModel, ProductOption } from '@models/product';
import {
  buildCalendarCartLine,
  CalendarCartAdditionalOption,
  CalendarCartLine,
  getCalendarOptionUnitFee,
  getDateStepIndex,
  getProductSaleFee,
  getTodayDateKey,
  groupOptionsByDateKey,
  isDateClosingSoon,
  isDateDisabled,
  isDatePast,
  isDateSoldOut,
  isTimeStepValue,
  splitAmPmTimeValues,
} from '@utils/product-option-date';
import { withObjectParticle } from '@utils/string';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { DividerBold } from './Divider';

const PRIMARY = 'var(--Primary-95, #FF3D8F)';
const SECTION_DIVIDER_STYLE: React.CSSProperties = {
  marginLeft: -20,
  marginRight: -20,
  width: 'calc(100% + 40px)',
};

interface ProductCalendarOptionSectionProps {
  product: ProductModel;
  onAddToCart: (line: CalendarCartLine) => void;
  /** 증가할 때마다 캘린더 아코디언을 펼침 (담기 / 바로구매 시 스크롤 포커스용) */
  focusToken?: number;
}

const AccordionHeader: React.FC<{ title: string; open: boolean; onToggle: () => void }> = ({
  title,
  open,
  onToggle,
}) => (
  <button
    type="button"
    className="link-button"
    onClick={onToggle}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      padding: '16px 0',
      color: '#242424',
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      textAlign: 'left',
    }}>
    <span>{title}</span>
    <ChevronUpIcon
      width={20}
      height={20}
      style={{
        flexShrink: 0,
        transform: open ? 'none' : 'rotate(180deg)',
        transition: 'transform 0.2s',
      }}
    />
  </button>
);

const ChipButton: React.FC<{
  label: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}> = ({ label, selected, disabled, onClick }) => (
  <button
    type="button"
    className="link-button"
    disabled={disabled}
    onClick={onClick}
    style={{
      minWidth: '64px',
      height: '40px',
      padding: '0 14px',
      borderRadius: '8px',
      border: selected ? `1px solid ${PRIMARY}` : '1px solid #DADCDF',
      background: selected ? PRIMARY : disabled ? '#F5F7FB' : '#FFFFFF',
      color: selected ? '#FFFFFF' : disabled ? '#B9BBBE' : '#242424',
      fontSize: '14px',
      fontWeight: selected ? 700 : 400,
      opacity: 1,
      cursor: disabled ? 'default' : 'pointer',
    }}>
    {label}
  </button>
);

export const ProductCalendarOptionSection: React.FC<ProductCalendarOptionSectionProps> = ({
  product,
  onAddToCart,
  focusToken,
}) => {
  const options = useMemo(
    () => (product.productOptions || []).filter((item) => item.type === 'sequential'),
    [product.productOptions],
  );
  const optionLabels = product.optionLabels || [];
  const purchaseQuantityOptions = product.purchaseQuantityOptions || [];
  const optionDescription = product.optionDescription;
  const requiredFixed = purchaseQuantityOptions.filter((item) => item.essential);
  const optionalFixed = purchaseQuantityOptions.filter((item) => !item.essential);
  const useRequiredQty = requiredFixed.length > 0;

  const optionsByDate = useMemo(() => groupOptionsByDateKey(options), [options]);
  const todayKey = getTodayDateKey();
  const productFee = useMemo(() => getProductSaleFee(product), [product]);
  const dateStepIndex = useMemo(() => (options[0] ? getDateStepIndex(options[0]) : 0), [options]);
  /** UI 라벨용 (optionLabels가 더 길 수 있음) */
  const stepDepth = useMemo(
    () => Math.max(...options.map((option) => option.step?.length || 0), optionLabels.length || 0, 1),
    [options, optionLabels.length],
  );
  /** 실제 옵션 step 깊이 — 마지막 step / 선택 완료 판정은 이 기준 (optionLabels로 부풀려지지 않음) */
  const optionStepDepth = useMemo(
    () => Math.max(0, ...options.map((option) => option.step?.length || 0)),
    [options],
  );

  /** 옵션이 존재하는 월 범위만 캘린더에 노출 (예: 7~9월) */
  const optionMonthRange = useMemo(() => {
    const keys = Array.from(optionsByDate.keys()).sort();
    if (keys.length === 0) {
      const today = moment().startOf('month');
      return {
        minDate: today.toDate(),
        maxDate: today.clone().endOf('month').toDate(),
        initialMonth: today.toDate(),
      };
    }
    const minKey = keys[0];
    const maxKey = keys[keys.length - 1];
    const minDate = moment(minKey).startOf('month').toDate();
    const maxDate = moment(maxKey).endOf('month').toDate();

    // 오늘이 옵션 월 범위 안이면 오늘 월, 아니면 첫 옵션 월부터
    const todayMonth = moment().startOf('month');
    const inRange = !todayMonth.isBefore(moment(minDate), 'month') && !todayMonth.isAfter(moment(maxDate), 'month');
    const initialMonth = inRange ? todayMonth.toDate() : minDate;

    return { minDate, maxDate, initialMonth };
  }, [optionsByDate]);

  const [calendarOpen, setCalendarOpen] = useState(true);
  const [stepOpenMap, setStepOpenMap] = useState<Record<number, boolean>>({});
  const [quantityOpen, setQuantityOpen] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedSteps, setSelectedSteps] = useState<(string | undefined)[]>([]);
  const [optionCount, setOptionCount] = useState(1);
  const [requiredCounts, setRequiredCounts] = useState<Record<number, number>>({});
  const [optionalCounts, setOptionalCounts] = useState<Record<number, number>>({});
  const [activeStartDate, setActiveStartDate] = useState<Date>(optionMonthRange.initialMonth);

  useEffect(() => {
    setSelectedDateKey(null);
    setSelectedSteps([]);
    setOptionCount(1);
    setRequiredCounts({});
    setOptionalCounts({});
    setActiveStartDate(optionMonthRange.initialMonth);
    setCalendarOpen(true);
    setStepOpenMap({});
    setQuantityOpen(true);
  }, [product._id, optionMonthRange.initialMonth]);

  useEffect(() => {
    if (focusToken == null || focusToken <= 0) return;
    setCalendarOpen(true);
  }, [focusToken]);

  const dayOptions = useMemo(() => {
    if (!selectedDateKey) return [] as ProductOption[];
    return optionsByDate.get(selectedDateKey) || [];
  }, [optionsByDate, selectedDateKey]);

  /** 날짜 step 다음으로 시간 step이 있으면 달력과 같은 섹션에서 연이어 선택 */
  const timeStepIndex = useMemo(() => {
    if (!options.length) return -1;
    for (let i = 0; i < optionStepDepth; i++) {
      if (i === dateStepIndex) continue;
      const values = options.map((option) => option.step?.[i]).filter(Boolean) as string[];
      if (values.length > 0 && values.every((value) => isTimeStepValue(value))) {
        return i;
      }
    }
    return -1;
  }, [options, optionStepDepth, dateStepIndex]);

  /**
   * 캘린더 UX 기준 마지막 step (날짜→시간→나머지 index 순).
   * 배열상 마지막 index가 아니라, 사용자가 맨 마지막에 고르는 step.
   * - 날짜/시간 외 remaining이 있으면 그중 가장 index
   * - 없으면 시간 step (날짜+시간만 있는 상품)
   */
  const lastUxStepIndex = useMemo(() => {
    let lastRemaining = -1;
    for (let i = 0; i < optionStepDepth; i++) {
      if (i === dateStepIndex || i === timeStepIndex) continue;
      lastRemaining = i;
    }
    if (lastRemaining >= 0) return lastRemaining;
    return timeStepIndex;
  }, [optionStepDepth, dateStepIndex, timeStepIndex]);

  /** 해당 날짜의 step 값 전체 (비마지막 step 목록 — 숨기지 않음) */
  const getAllDayStepValues = (stepIndex: number): string[] => {
    return Array.from(
      new Set(dayOptions.map((option) => option.step?.[stepIndex]).filter(Boolean) as string[]),
    );
  };

  /** 현재 step 제외, 이미 선택된 모든 step과 경로가 맞는 옵션 */
  const matchesOtherSelectedSteps = (option: ProductOption, stepIndex: number): boolean => {
    return selectedSteps.every((selected, idx) => {
      if (idx === stepIndex) return true;
      if (selected == null || selected === '') return true;
      return option.step?.[idx] === selected;
    });
  };

  /** 마지막 UX step 목록 — 조합 불가 미노출, 매진 포함 */
  const getCompatibleStepValues = (stepIndex: number): string[] => {
    return Array.from(
      new Set(
        dayOptions
          .filter((option) => matchesOtherSelectedSteps(option, stepIndex) && Boolean(option.step?.[stepIndex]))
          .map((option) => option.step?.[stepIndex])
          .filter(Boolean) as string[],
      ),
    );
  };

  /**
   * 마지막 UX step만 조합 가능 칩 / 그 외(시간 포함)는 날짜 기준 전체.
   * 시간이 배열 끝이어도 remaining이 있으면 시간은 비마지막 → 목록이 줄지 않음.
   */
  const getStepValues = (stepIndex: number): string[] => {
    if (stepIndex === lastUxStepIndex) {
      return getCompatibleStepValues(stepIndex);
    }
    return getAllDayStepValues(stepIndex);
  };

  /** 선택된 다른 step + 해당 값으로 매칭 (매진 판정용) */
  const getMatchingOptionsForStepValue = (stepIndex: number, value: string): ProductOption[] => {
    return dayOptions.filter((option) => {
      if (option.step?.[stepIndex] !== value) return false;
      return matchesOtherSelectedSteps(option, stepIndex);
    });
  };

  /** 시간 칩: UX 마지막이 아니면 항상 그날 전체 유지 */
  const timeStepValues =
    timeStepIndex < 0 || dayOptions.length === 0 ? ([] as string[]) : getStepValues(timeStepIndex);

  const matchedOptions = useMemo(() => {
    return dayOptions.filter((option) =>
      selectedSteps.every((value, index) => {
        if (value == null || value === '') return true;
        return option.step?.[index] === value;
      }),
    );
  }, [dayOptions, selectedSteps]);

  const resolvedOption = useMemo(() => {
    for (let i = 0; i < optionStepDepth; i++) {
      if (i === dateStepIndex) continue;
      if (!selectedSteps[i]) return undefined;
    }
    return matchedOptions[0];
  }, [matchedOptions, selectedSteps, optionStepDepth, dateStepIndex]);

  const nextStepIndex = useMemo(() => {
    for (let i = 0; i < optionStepDepth; i++) {
      if (i === dateStepIndex) continue;
      if (i === timeStepIndex) continue;
      if (!selectedSteps[i]) return i;
    }
    return -1;
  }, [selectedSteps, optionStepDepth, dateStepIndex, timeStepIndex]);

  /**
   * 매진만 비활성.
   * - 비마지막: 목록은 전체 유지, 매칭되는 옵션이 모두 재고 0이면 disable
   * - 마지막: 조합 불가는 미렌더, 노출된 칩 중 매진만 disable
   * 비마지막에서 다른 선택과 안 맞아 matching이 비면 매진이 아니므로 disable하지 않음
   */
  const isStepValueDisabled = (stepIndex: number, value: string): boolean => {
    const matching = getMatchingOptionsForStepValue(stepIndex, value);
    if (matching.length === 0) {
      // 비마지막: 조합 불일치여도 칩은 보여 주되 매진으로 보지 않음
      // 마지막: 목록에 안 들어오므로 여기 거의 안 탐
      if (stepIndex === lastUxStepIndex) return true;
      return false;
    }
    return matching.every((option) => (option.inventory ?? 0) <= 0);
  };

  // 새로 나타난 step / 수량 섹션은 기본 펼침
  useEffect(() => {
    if (nextStepIndex < 0) return;
    if (nextStepIndex === dateStepIndex || nextStepIndex === timeStepIndex) return;
    setStepOpenMap((prev) => {
      if (prev[nextStepIndex] !== undefined) return prev;
      return { ...prev, [nextStepIndex]: true };
    });
  }, [nextStepIndex, dateStepIndex, timeStepIndex]);

  useEffect(() => {
    if (resolvedOption) {
      setQuantityOpen(true);
      const inv = resolvedOption.inventory ?? 0;
      setOptionCount(inv > 0 ? 1 : 0);
    }
  }, [resolvedOption]);

  const inventoryCap = resolvedOption?.inventory ?? 0;

  const selectionSummary = useMemo(() => {
    if (!resolvedOption) return '';
    const parts = (resolvedOption.step || []).filter(Boolean);
    const qtyLabel = useRequiredQty
      ? requiredFixed
          .map((item, index) => {
            const count = requiredCounts[index] || 0;
            return count > 0 ? `${item.title} X${count}` : null;
          })
          .filter(Boolean)
          .join(' / ')
      : `X${optionCount}`;
    const optionLine = `옵션-${parts.join(' / ')}${qtyLabel ? ` ${qtyLabel}` : ''}`;
    const additional = optionalFixed
      .map((item, index) => {
        const count = optionalCounts[index] || 0;
        return count > 0 ? `${item.title} X${count}` : null;
      })
      .filter(Boolean)
      .join(' / ');
    return additional ? `${optionLine}\n추가 옵션-${additional}` : optionLine;
  }, [resolvedOption, useRequiredQty, requiredFixed, requiredCounts, optionCount, optionalFixed, optionalCounts]);

  const resolvedUnitFee = useMemo(() => {
    if (!resolvedOption) return 0;
    return getCalendarOptionUnitFee(resolvedOption, productFee);
  }, [resolvedOption, productFee]);

  const lineTotal = useMemo(() => {
    if (!resolvedOption) return 0;
    if (useRequiredQty) {
      const requiredQty = requiredFixed.reduce((sum, _, index) => sum + (requiredCounts[index] || 0), 0);
      const main = resolvedUnitFee * requiredQty;
      const optional = optionalFixed.reduce(
        (sum, item, index) => sum + (item.fee || 0) * (optionalCounts[index] || 0),
        0,
      );
      return main + optional;
    }
    const main = resolvedUnitFee * optionCount;
    const optional = optionalFixed.reduce(
      (sum, item, index) => sum + (item.fee || 0) * (optionalCounts[index] || 0),
      0,
    );
    return main + optional;
  }, [
    resolvedOption,
    resolvedUnitFee,
    useRequiredQty,
    requiredFixed,
    requiredCounts,
    optionalFixed,
    optionalCounts,
    optionCount,
  ]);

  const canAdd = useMemo(() => {
    if (!resolvedOption) return false;
    if ((resolvedOption.inventory ?? 0) <= 0) return false;
    if (useRequiredQty) {
      return requiredFixed.some((_, index) => (requiredCounts[index] || 0) > 0);
    }
    return optionCount > 0;
  }, [resolvedOption, useRequiredQty, requiredFixed, requiredCounts, optionCount]);

  const resetSelectionForm = () => {
    setSelectedDateKey(null);
    setSelectedSteps([]);
    setOptionCount(1);
    setRequiredCounts({});
    setOptionalCounts({});
  };

  const handleSelectDate = (date: Date) => {
    const key = moment(date).format('YYYY-MM-DD');
    const dayList = optionsByDate.get(key) || [];
    if (isDateDisabled(key, dayList, todayKey)) return;

    const dateLabel = dayList[0]?.step?.[dateStepIndex];
    const nextSteps: (string | undefined)[] = Array.from({ length: stepDepth });
    if (dateLabel) {
      nextSteps[dateStepIndex] = dateLabel;
    }
    setSelectedDateKey(key);
    setSelectedSteps(nextSteps);
    setOptionCount(1);
    setRequiredCounts({});
    setOptionalCounts({});
  };

  const handleSelectStepValue = (stepIndex: number, value: string) => {
    if (isStepValueDisabled(stepIndex, value)) return;

    setSelectedSteps((prev) => {
      const next = [...prev];
      next[stepIndex] = value;

      if (stepIndex === timeStepIndex) {
        // 시간 변경 시: 날짜·시간 외 나머지 옵션만 초기화
        for (let i = 0; i < stepDepth; i++) {
          if (i === dateStepIndex || i === timeStepIndex) continue;
          next[i] = undefined;
        }
        return next;
      }

      // 일반 옵션 선택 시: 날짜·시간은 유지하고, 현재 step 이후 옵션만 초기화
      for (let i = 0; i < stepDepth; i++) {
        if (i === dateStepIndex || i === timeStepIndex) continue;
        if (i > stepIndex) next[i] = undefined;
      }
      return next;
    });
  };

  const handleAdd = () => {
    if (!resolvedOption || !canAdd) return;

    let count = optionCount;
    const additional: CalendarCartAdditionalOption[] = [];

    if (useRequiredQty) {
      count = requiredFixed.reduce((sum, _, index) => sum + (requiredCounts[index] || 0), 0);
      // Represent required titles in summary via optionCount; fee stays on main option
    }

    optionalFixed.forEach((item, index) => {
      const qty = optionalCounts[index] || 0;
      if (qty > 0) {
        additional.push({ name: item.title || '', count: qty, fee: item.fee || 0 });
      }
    });

    // Include essential fixed titles in additional only when they have separate fees?
    // CommerceOptionModal treats required as separate selected options with product fee.
    // For cart display Figma shows "1회 체험권 X1" as part of option line.
    // Keep main optionCount; if required fixed used, encode in optionCount.

    onAddToCart(
      buildCalendarCartLine({
        option: resolvedOption,
        optionCount: Math.max(count, 1),
        additionalOptions: additional,
        productFee,
      }),
    );
    resetSelectionForm();
  };

  const renderTimeSlots = () => {
    if (timeStepIndex < 0 || !selectedDateKey || timeStepValues.length === 0) return null;
    const { am, pm, other } = splitAmPmTimeValues(timeStepValues);
    const formatTimeLabel = (value: string) =>
      value
        .replace(/^오전\s*/, '')
        .replace(/^오후\s*/, '')
        .replace(/^AM\s*/i, '')
        .replace(/^PM\s*/i, '');

    return (
      <div style={{ marginTop: '16px' }}>
        {am.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ color: '#66686B', fontSize: '13px', marginBottom: '8px' }}>오전</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {am.map((value) => (
                <ChipButton
                  key={value}
                  label={formatTimeLabel(value)}
                  selected={selectedSteps[timeStepIndex] === value}
                  disabled={isStepValueDisabled(timeStepIndex, value)}
                  onClick={() => handleSelectStepValue(timeStepIndex, value)}
                />
              ))}
            </div>
          </div>
        )}
        {pm.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ color: '#66686B', fontSize: '13px', marginBottom: '8px' }}>오후</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {pm.map((value) => (
                <ChipButton
                  key={value}
                  label={formatTimeLabel(value)}
                  selected={selectedSteps[timeStepIndex] === value}
                  disabled={isStepValueDisabled(timeStepIndex, value)}
                  onClick={() => handleSelectStepValue(timeStepIndex, value)}
                />
              ))}
            </div>
          </div>
        )}
        {other.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {other.map((value) => (
              <ChipButton
                key={value}
                label={formatTimeLabel(value)}
                selected={selectedSteps[timeStepIndex] === value}
                disabled={isStepValueDisabled(timeStepIndex, value)}
                onClick={() => handleSelectStepValue(timeStepIndex, value)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderQuantityBlock = (
    items: CommerceFixedOptionItem[],
    counts: Record<number, number>,
    setCounts: React.Dispatch<React.SetStateAction<Record<number, number>>>,
    title?: string,
    unitFeeOverride?: number,
  ) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom: '16px' }}>
        {title ? (
          <div style={{ color: '#242424', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>{title}</div>
        ) : null}
        {items.map((item, index) => {
          const displayFee = typeof unitFeeOverride === 'number' && item.essential ? unitFeeOverride : item.fee || 0;
          return (
            <div
              key={`${title || 'qty'}-${index}`}
              style={{
                border: '1px solid #EFF1F4',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '8px',
              }}>
              <div style={{ color: '#242424', fontSize: '15px', marginBottom: '8px' }}>{item.title}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '16px' }}>{displayFee.toLocaleString()}원</div>
                <NumberEdit
                  min={0}
                  max={99}
                  value={counts[index] || 0}
                  onClickMinus={(value) => setCounts((prev) => ({ ...prev, [index]: value }))}
                  onClickPlus={(value) => setCounts((prev) => ({ ...prev, [index]: value }))}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: '0 20px 24px', borderTop: '8px solid #F5F7FB' }}>
      <AccordionHeader
        title="날짜와 시간을 선택해 주세요"
        open={calendarOpen}
        onToggle={() => setCalendarOpen((prev) => !prev)}
      />
      {calendarOpen && (
        <div className="product-option-calendar" style={{ marginBottom: '16px' }}>
          <Calendar
            className="product-option-calendar__widget"
            calendarType="gregory"
            locale="ko-KR"
            view="month"
            maxDetail="month"
            minDetail="month"
            value={selectedDateKey ? moment(selectedDateKey).toDate() : null}
            minDate={optionMonthRange.minDate}
            maxDate={optionMonthRange.maxDate}
            activeStartDate={activeStartDate}
            onActiveStartDateChange={({ activeStartDate: next }) => {
              if (!next) return;
              const nextMonth = moment(next).startOf('month');
              if (nextMonth.isBefore(moment(optionMonthRange.minDate), 'month')) return;
              if (nextMonth.isAfter(moment(optionMonthRange.maxDate), 'month')) return;
              setActiveStartDate(next);
            }}
            onClickDay={handleSelectDate}
            tileDisabled={({ date, view }) => {
              if (view !== 'month') return false;
              const key = moment(date).format('YYYY-MM-DD');
              const dayList = optionsByDate.get(key) || [];
              if (!optionsByDate.has(key)) return true;
              return isDateDisabled(key, dayList, todayKey);
            }}
            tileContent={({ date, view }) => {
              if (view !== 'month') return null;
              const key = moment(date).format('YYYY-MM-DD');
              const dayList = optionsByDate.get(key) || [];
              if (dayList.length === 0) return null;

              const isSelected = selectedDateKey === key;
              // 매진 표기 임시 비노출 (매진 날짜도 클릭 가능, 이후 step chip에서 비활성)
              // if (isDateSoldOut(dayList)) {
              //   return (
              //     <div
              //       style={{
              //         fontSize: '10px',
              //         fontWeight: 600,
              //         color: isSelected ? '#FFFFFF' : '#B9BBBE',
              //         lineHeight: 1,
              //         marginTop: 2,
              //         textAlign: 'center',
              //       }}>
              //       매진
              //     </div>
              //   );
              // }
              // CommerceOptionModal 마감임박(잔여 1~9)과 동일 조건 → 캘린더는 '임박' 표기 (지난 날짜 제외)
              if (
                !isDatePast(key, todayKey) &&
                !isDateSoldOut(dayList) &&
                isDateClosingSoon(dayList)
              ) {
                return (
                  <div
                    className="calendar-option-caption"
                    style={{
                      color: isSelected ? '#FFFFFF' : '#9B9DA0',
                    }}>
                    임박
                  </div>
                );
              }
              return null;
            }}
            tileClassName={({ date, view }) => {
              if (view !== 'month') return null;
              const key = moment(date).format('YYYY-MM-DD');
              if (selectedDateKey === key) return 'calendar-option-selected';
              return null;
            }}
            prev2Label={null}
            next2Label={null}
            prevLabel={<ChevronLeftIcon />}
            nextLabel={<ChevronRightIcon />}
            showNeighboringMonth={false}
            formatDay={(_locale, date) => date.getDate().toString()}
            navigationLabel={({ date }) => moment(date).format('YYYY.MM')}
          />
          <style>{`
            .product-option-calendar .react-calendar {
              width: 100%;
              border: none;
              font-family: Pretendard, sans-serif;
            }
            .product-option-calendar .react-calendar__navigation__arrow:disabled {
              opacity: 0.3 !important;
              cursor: not-allowed !important;
              background-color: transparent !important;
            }
            .product-option-calendar .react-calendar__navigation__arrow:disabled svg path {
              stroke: #D5D8DD !important;
            }
            .product-option-calendar .react-calendar__navigation__label:disabled {
              cursor: default !important;
              pointer-events: none !important;
              background-color: transparent !important;
            }
            /* Figma Day: px 10 / py 4, date top-aligned for caption below */
            .product-option-calendar .react-calendar__tile {
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: center;
              padding: 4px 10px;
              font-size: 15px;
              border-radius: 8px !important;
            }
            .product-option-calendar .react-calendar__tile abbr {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 24px;
              height: 24px;
              text-decoration: none;
              line-height: 1;
            }
            .product-option-calendar .react-calendar__tile .calendar-option-caption {
              width: 100%;
              font-size: 10px;
              font-weight: 600;
              line-height: normal;
              text-align: center;
            }
            .product-option-calendar .react-calendar__tile:disabled {
              background: transparent;
              color: #B9BBBE;
            }
            .product-option-calendar .react-calendar__tile:enabled:hover,
            .product-option-calendar .react-calendar__tile:enabled:focus {
              border-radius: 8px !important;
            }
            .product-option-calendar .react-calendar__tile--active,
            .product-option-calendar .react-calendar__tile--active:enabled:hover,
            .product-option-calendar .react-calendar__tile--active:enabled:focus,
            .product-option-calendar .react-calendar__tile.calendar-option-selected,
            .product-option-calendar .react-calendar__tile.calendar-option-selected:enabled:hover,
            .product-option-calendar .react-calendar__tile.calendar-option-selected:enabled:focus {
              background: ${PRIMARY} !important;
              color: #fff !important;
              border-radius: 8px !important;
            }
            .product-option-calendar .react-calendar__tile--now {
              background: transparent;
            }
            .product-option-calendar .react-calendar__tile--now:enabled:hover {
              border-radius: 8px !important;
            }
          `}</style>
          {/* 날짜 선택 직후 같은 섹션에서 시간 연이어 선택 */}
          {renderTimeSlots()}
        </div>
      )}

      {/* 시간 외 나머지 sequential step (예: 시대) */}
      {(() => {
        const showRemainingSteps =
          Boolean(selectedDateKey) && (timeStepIndex < 0 || Boolean(selectedSteps[timeStepIndex]));
        if (!showRemainingSteps) return null;

        const remainingIndexes = Array.from({ length: optionStepDepth }, (_, index) => index).filter((index) => {
          if (index === dateStepIndex || index === timeStepIndex) return false;
          if (index !== nextStepIndex && !selectedSteps[index]) return false;
          return getStepValues(index).length > 0;
        });

        if (remainingIndexes.length === 0) return null;

        return (
          <>
            <DividerBold style={{ ...SECTION_DIVIDER_STYLE, marginTop: 8, marginBottom: 8 }} />
            {remainingIndexes.map((index, listIndex) => {
              const label = optionLabels[index] || `옵션 ${index + 1}`;
              const title = `${withObjectParticle(label)} 선택해 주세요`;
              const open = stepOpenMap[index] !== false;
              const values = getStepValues(index);

              return (
                <div key={`step-${index}`}>
                  {listIndex > 0 && <DividerBold style={{ ...SECTION_DIVIDER_STYLE, marginTop: 8, marginBottom: 8 }} />}
                  <AccordionHeader
                    title={title}
                    open={open}
                    onToggle={() =>
                      setStepOpenMap((prev) => ({
                        ...prev,
                        [index]: !(prev[index] !== false),
                      }))
                    }
                  />
                  {open && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingBottom: 16 }}>
                      {values.map((value) => (
                        <ChipButton
                          key={value}
                          label={value}
                          selected={selectedSteps[index] === value}
                          disabled={isStepValueDisabled(index, value)}
                          onClick={() => handleSelectStepValue(index, value)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        );
      })()}

      {resolvedOption && (
        <>
          <DividerBold style={{ ...SECTION_DIVIDER_STYLE, marginTop: 8, marginBottom: 8 }} />
          <AccordionHeader title="수량 선택" open={quantityOpen} onToggle={() => setQuantityOpen((prev) => !prev)} />
          {quantityOpen && (
            <>
              {useRequiredQty ? (
                renderQuantityBlock(requiredFixed, requiredCounts, setRequiredCounts, undefined, resolvedUnitFee)
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      marginBottom: '8px',
                    }}>
                    <div style={{ color: '#424242', fontSize: '16px', lineHeight: '22px', fontWeight: 600 }}>
                      {(resolvedOption.step || []).filter(Boolean).join(' / ') || '옵션'}
                    </div>
                    {optionDescription ? (
                      <div style={{ color: '#808387', fontSize: '14px', lineHeight: '20px', fontWeight: 400 }}>
                        {optionDescription}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ color: '#242424', fontSize: '14px', lineHeight: '20px', fontWeight: 700 }}>
                      {resolvedUnitFee.toLocaleString()}원
                    </div>
                    <NumberEdit
                      min={inventoryCap > 0 ? 1 : 0}
                      max={inventoryCap}
                      value={inventoryCap > 0 ? optionCount : 0}
                      disabled={inventoryCap <= 0}
                      onClickMinus={setOptionCount}
                      onClickPlus={setOptionCount}
                    />
                  </div>
                </div>
              )}

              {renderQuantityBlock(optionalFixed, optionalCounts, setOptionalCounts, '추가 옵션')}

              {/* 수량 선택 카드와 담기 버튼 사이 검정 divider (#242424) */}
              <div style={{ height: 1, width: '100%', backgroundColor: '#242424', margin: '16px 0' }} />

              {selectionSummary && (
                <div
                  style={{
                    whiteSpace: 'pre-line',
                    color: '#66686B',
                    fontSize: '13px',
                    lineHeight: '20px',
                    marginBottom: '12px',
                  }}>
                  {selectionSummary}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}>
                <span style={{ fontSize: '15px', color: '#242424' }}>합계</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#242424' }}>
                  {lineTotal.toLocaleString()}원
                </span>
              </div>

              <button
                type="button"
                className="link-button"
                disabled={!canAdd}
                onClick={handleAdd}
                style={{
                  width: '100%',
                  height: '56px',
                  borderRadius: '8px',
                  background: canAdd ? '#242424' : '#D5D8DD',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 700,
                }}>
                담기
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};
